import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import './P2pTradeBuyWaitingSeller.css';

const mockOrderData = {
  orderId: '5523676727227499',
  merchant: {
    name: 'Fast Trader',
    initial: 'F',
    verified: true,
    fullName: 'Fast Trader'
  },
  timeRemaining: 796, // seconds (13:16)
  orderDetails: {
    quantity: '1.00',
    crypto: 'USDT',
    amountFiat: '1450.00',
    currency: 'NGN',
    price: '1450.00',
    priceUnit: 'NGN/USDT'
  },
  paymentMethod: {
    bankName: 'OPay',
    accountName: 'Dayo Odewade',
    bank: 'OPay',
    accountNumber: '09951629018'
  }
};

const P2pTradeBuyWaitingSeller: React.FC = () => {
  const navigate = useNavigate();
  const [timeRemaining, setTimeRemaining] = useState(mockOrderData.timeRemaining);

  useEffect(() => {
    const timer = setInterval(() => {
      setTimeRemaining((prev) => {
        if (prev <= 0) {
          clearInterval(timer);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  const formatTime = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Progress is approximately 66.7% based on design
  const progressPercentage = 66.7;

  const handleBack = () => {
    navigate(-1);
  };

  const handleChat = () => {
    console.log('Opening chat...');
  };

  const handleCopyOrderId = () => {
    navigator.clipboard.writeText(mockOrderData.orderId);
  };

  const handleCopyAccountName = () => {
    navigator.clipboard.writeText(mockOrderData.paymentMethod.accountName);
  };

  const handleCopyBank = () => {
    navigator.clipboard.writeText(mockOrderData.paymentMethod.bank);
  };

  const handleCopyAccountNumber = () => {
    navigator.clipboard.writeText(mockOrderData.paymentMethod.accountNumber);
  };

  const handleDispute = () => {
    console.log('Dispute opened');
  };

  return (
    <div className="waiting-seller-container">
      <header className="waiting-seller-header">
        <button className="waiting-seller-back-button" onClick={handleBack} aria-label="Go back">
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
            <path d="M10 12L6 8L10 4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </button>
        
        <h1 className="waiting-seller-title">Buy USDT</h1>

        <button className="waiting-seller-chat-button" onClick={handleChat} aria-label="Chat">
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
            <path d="M17 8.5C17 12.366 13.866 15.5 10 15.5C8.95 15.5 7.951 15.28 7.049 14.888L3 16L4.112 11.951C3.72 11.049 3.5 10.05 3.5 9C3.5 5.134 6.634 2 10.5 2C14.366 2 17.5 5.134 17.5 9" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
          <div className="waiting-seller-chat-badge">2</div>
        </button>
      </header>

      <div className="waiting-seller-progress-container">
        <div className="waiting-seller-progress-bar">
          <div 
            className="waiting-seller-progress-fill" 
            style={{ width: `${progressPercentage}%` }}
          />
        </div>
      </div>

      <div className="waiting-seller-content">
        <div className="waiting-seller-merchant-card">
          <div className="waiting-seller-merchant-avatar">
            {mockOrderData.merchant.initial}
          </div>
          
          <div className="waiting-seller-merchant-info">
            <div className="waiting-seller-merchant-name">
              {mockOrderData.merchant.name}
              {mockOrderData.merchant.verified && (
                <svg className="waiting-seller-verified-badge" width="16" height="16" viewBox="0 0 16 16" fill="none">
                  <path d="M8 0L9.2 5.2L14.4 6.4L9.2 7.6L8 12.8L6.8 7.6L1.6 6.4L6.8 5.2L8 0Z" fill="#22c35d"/>
                </svg>
              )}
            </div>
            <div className="waiting-seller-merchant-label">Merchant</div>
          </div>

          <h2 className="waiting-seller-order-heading">Verifying Payment</h2>
          
          <p className="waiting-seller-subtitle">
            Please wait while the payment is being verified.
          </p>

          <div className="waiting-seller-timer-display">{formatTime(timeRemaining)}</div>
        </div>

        <div className="waiting-seller-order-id-card">
          <div className="waiting-seller-order-id-label">ID: {mockOrderData.orderId}</div>
          <button 
            className="waiting-seller-copy-button" 
            onClick={handleCopyOrderId}
            aria-label="Copy order ID"
          >
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
              <rect x="5" y="5" width="9" height="9" rx="1.5" stroke="currentColor" strokeWidth="1.5"/>
              <path d="M3 11V3C3 2.44772 3.44772 2 4 2H10" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
            </svg>
          </button>
        </div>

        <div className="waiting-seller-details-section">
          <div className="waiting-seller-detail-row">
            <span className="waiting-seller-detail-label">Quantity</span>
            <div className="waiting-seller-detail-value-group">
              <span className="waiting-seller-detail-amount">{mockOrderData.orderDetails.quantity}</span>
              <span className="waiting-seller-detail-crypto">{mockOrderData.orderDetails.crypto}</span>
            </div>
          </div>

          <div className="waiting-seller-detail-row">
            <span className="waiting-seller-detail-label">Amount (Fiat)</span>
            <div className="waiting-seller-detail-value-group">
              <span className="waiting-seller-detail-amount">{mockOrderData.orderDetails.amountFiat}</span>
              <span className="waiting-seller-detail-currency">{mockOrderData.orderDetails.currency}</span>
            </div>
          </div>

          <div className="waiting-seller-detail-row">
            <span className="waiting-seller-detail-label">Price</span>
            <div className="waiting-seller-detail-value-group">
              <span className="waiting-seller-detail-amount">{mockOrderData.orderDetails.price}</span>
              <span className="waiting-seller-detail-unit">{mockOrderData.orderDetails.priceUnit}</span>
            </div>
          </div>
        </div>

        <div className="waiting-seller-payment-card">
          <div className="waiting-seller-payment-merchant">
            <span className="waiting-seller-payment-label">Merchant Name</span>
            <span className="waiting-seller-payment-value">{mockOrderData.merchant.fullName}</span>
          </div>

          <div className="waiting-seller-payment-method-box">
            <div className="waiting-seller-bank-header">
              <span className="waiting-seller-bank-name">{mockOrderData.paymentMethod.bankName}</span>
              <div className="waiting-seller-bank-badge" />
            </div>

            <div className="waiting-seller-payment-info-section">
              <div className="waiting-seller-payment-info-row">
                <span className="waiting-seller-payment-info-label">Account Name</span>
                <span className="waiting-seller-payment-info-value">{mockOrderData.paymentMethod.accountName}</span>
                <button 
                  className="waiting-seller-copy-icon-button" 
                  onClick={handleCopyAccountName}
                  aria-label="Copy account name"
                >
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                    <rect x="5" y="5" width="9" height="9" rx="1.5" stroke="currentColor" strokeWidth="1.5"/>
                    <path d="M3 11V3C3 2.44772 3.44772 2 4 2H10" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                  </svg>
                </button>
              </div>

              <div className="waiting-seller-payment-info-row">
                <span className="waiting-seller-payment-info-label">Bank</span>
                <span className="waiting-seller-payment-info-value">{mockOrderData.paymentMethod.bank}</span>
                <button 
                  className="waiting-seller-copy-icon-button" 
                  onClick={handleCopyBank}
                  aria-label="Copy bank"
                >
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                    <rect x="5" y="5" width="9" height="9" rx="1.5" stroke="currentColor" strokeWidth="1.5"/>
                    <path d="M3 11V3C3 2.44772 3.44772 2 4 2H10" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                  </svg>
                </button>
              </div>

              <div className="waiting-seller-payment-info-row">
                <span className="waiting-seller-payment-info-label">Account Number</span>
                <span className="waiting-seller-payment-info-value waiting-seller-account-number">{mockOrderData.paymentMethod.accountNumber}</span>
                <button 
                  className="waiting-seller-copy-icon-button" 
                  onClick={handleCopyAccountNumber}
                  aria-label="Copy account number"
                >
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                    <rect x="5" y="5" width="9" height="9" rx="1.5" stroke="currentColor" strokeWidth="1.5"/>
                    <path d="M3 11V3C3 2.44772 3.44772 2 4 2H10" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                  </svg>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="waiting-seller-action-bar">
        <button className="waiting-seller-primary-button" disabled>
          Payment Sent - Waiting for Seller
        </button>
        
        <button className="waiting-seller-dispute-button" onClick={handleDispute}>
          Dispute
        </button>
      </div>
    </div>
  );
};

export default P2pTradeBuyWaitingSeller;